import type { BreathingStyle } from '@/types/breathing';
import type {
  BlogPostRow,
  BlogPostInsert,
  BlogPostUpdate,
} from '@/types/database.types';
import { supabase } from './supabase';

/**
 * API Client Utilities
 *
 * This module provides type-safe fetch wrappers for making API requests.
 * Configure the base URL in your .env file as VITE_API_URL
 */

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

/**
 * Generic API Response type
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
  error?: string;
}

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper with typed responses
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.message || 'An error occurred',
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, 'Network error or server unavailable');
  }
}

/**
 * GET request
 */
export async function get<T>(endpoint: string): Promise<ApiResponse<T>> {
  return fetchApi<T>(endpoint, { method: 'GET' });
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<T>> {
  return fetchApi<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<T>> {
  return fetchApi<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE request
 */
export async function del<T>(endpoint: string): Promise<ApiResponse<T>> {
  return fetchApi<T>(endpoint, { method: 'DELETE' });
}

// Example API functions (uncomment and customize as needed)

/**
 * User API
 */
// export interface User {
//   id: string;
//   name: string;
//   email: string;
// }

// export const userApi = {
//   getProfile: () => get<User>('/user/profile'),
//   updateProfile: (data: Partial<User>) => put<User>('/user/profile', data),
// };

/**
 * Breathing Session API
 */
// export interface BreathingSession {
//   id: string;
//   userId: string;
//   date: string;
//   duration: number;
//   ratio: string;
// }

// export const sessionApi = {
//   getSessions: () => get<BreathingSession[]>('/sessions'),
//   createSession: (data: Omit<BreathingSession, 'id'>) =>
//     post<BreathingSession>('/sessions', data),
// };

/**
 * Leaderboard API
 */
// export interface LeaderboardEntry {
//   rank: number;
//   userId: string;
//   name: string;
//   totalMinutes: number;
//   sessions: number;
// }

// export const leaderboardApi = {
//   getLeaderboard: (limit = 10) =>
//     get<LeaderboardEntry[]>(`/leaderboard?limit=${limit}`),
// };

// Session Template types (for potential future REST endpoints; currently using Supabase client directly)
export interface SessionTemplate {
  id: string;
  user_id: string;
  template_name: string;
  voice_guidance: boolean;
  phase_chime: boolean;
  background_sound: boolean;
  music_enabled: boolean;
  music_track_id: string | null;
  intro_voice: boolean;
  duration: number | null;
  breathing_ratio_id: string | null;
  breathing_ratio_other_inhale?: number | null;
  breathing_ratio_other_exhale?: number | null;
  breathing_ratio_other_hold_full?: number | null;
  breathing_ratio_other_hold_empty?: number | null;
  background_videos_id: string | null;
  vision_board_id?: string | null;
  breathing_style: BreathingStyle;
  breathing_style_id?: string | null;
  affirmation?: boolean;
  affirmation_type?: string | null;
  affirmation_mode?: 'regular' | 'subliminal' | null;
  affirmation_whisper_mode?: boolean;
  play_in_background?: boolean;
  hide_cycle_count?: boolean;
  hide_time_left?: boolean;
  /** New column: background_play tells if template enables background playback */
  background_play?: boolean;
  created_at: string;
  updated_at: string;
}

export interface BreathingStyleRecord {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BreathingRatio {
  id: string;
  name: string;
  ratio: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BackgroundVideo {
  id: string;
  name: string;
  file_name: string;
  preview_image_url?: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MusicTrack {
  id: string;
  name: string;
  audio_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VisionBoardBackground {
  id: string;
  title: string;
  image_url: string;
  active: boolean;
  is_default: boolean;
  default_display: 'fill' | 'repeat';
  created_at: string;
}

export interface VisionBoardTemplate {
  id: string;
  title: string;
  description: string | null;
  preview_image_url: string | null;
  image_url: string | null;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VisionBoardImage {
  id: string;
  user_id: string;
  image_url: string;
  preview_image_url?: string | null;
  filename: string;
  title?: string | null;
  created_at: string;
}

export interface BreathingPreset {
  id: string;
  name: string;
  slug: string;
  inhale: number;
  hold_full: number;
  exhale: number;
  hold_empty: number;
  default_duration: number;
  default_background: string | null;
  default_audio: boolean;
  auto_start: boolean;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface Quote {
  id: string;
  text: string;
  author: string | null;
  category: 'motivation' | 'calm' | 'focus' | 'general' | 'wisdom';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FeedbackRecord {
  id: string;
  user_id: string | null;
  category: string;
  message: string;
  page: string | null;
  created_at: string;
}

// ============================================================================
// Blog Post Types & Supabase Helpers
// ============================================================================

export type BlogPost = BlogPostRow;
export type NewBlogPost = BlogPostInsert;
export type BlogPostChanges = BlogPostUpdate;

export async function fetchBlogPosts(
  includeDrafts = true
): Promise<BlogPost[]> {
  let query = supabase
    .from('blog_posts')
    .select('*')
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (!includeDrafts) {
    query = query.eq('is_published', true);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as BlogPost[];
}

export async function fetchBlogPostById(id: string): Promise<BlogPost> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function fetchBlogPostBySlug(
  slug: string,
  includeDrafts = false
): Promise<BlogPost | null> {
  let query = supabase.from('blog_posts').select('*').eq('slug', slug).limit(1);

  if (!includeDrafts) {
    query = query.eq('is_published', true);
  }

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data as BlogPost;
}

export async function createBlogPost(input: NewBlogPost): Promise<BlogPost> {
  const payload: BlogPostInsert = {
    title: input.title,
    slug: input.slug,
    excerpt: input.excerpt ?? null,
    content: input.content,
    cover_image: input.cover_image ?? null,
    is_published: Boolean(input.is_published),
    published_at:
      input.is_published && !input.published_at
        ? new Date().toISOString()
        : (input.published_at ?? null),
    author_id: input.author_id,
  };

  const { data, error } = await supabase
    .from('blog_posts')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function updateBlogPost(
  id: string,
  changes: BlogPostChanges
): Promise<BlogPost> {
  const payload: BlogPostUpdate = {
    ...changes,
  };

  if (typeof changes.is_published === 'boolean') {
    payload.published_at = changes.is_published
      ? (changes.published_at ?? new Date().toISOString())
      : null;
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function toggleBlogPostStatus(
  id: string,
  nextStatus: boolean
): Promise<BlogPost> {
  const { data, error } = await supabase
    .from('blog_posts')
    .update({
      is_published: nextStatus,
      published_at: nextStatus ? new Date().toISOString() : null,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function deleteBlogPost(id: string): Promise<void> {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  if (error) throw error;
}
